import React, { useState } from 'react';
import { Button } from '../shared';
import { dispatchBillCreation, updateBillLine } from '../../service';
import { popup } from '../../utils';
import { POPUP_TYPE, ROUTES } from '../../constants';
import { useNavigate } from "../../backend/navigation";

const ContainerThreadBoxEntry = ({ setCostviewOpen, state, isBill, setLineData }) => {
  const navigate = useNavigate();
  const [containerCount, setContainerCount] = useState('');
  const [threadCount, setThreadCount] = useState('');
  const [boxPolyCount, setBoxPolyCount] = useState('');
  const [containerData, setContainerData] = useState([]);
  const [threadData, setThreadData] = useState([]);
  const [boxPolyData, setBoxPolyData] = useState([]);
  const [showEntry, setShowEntry] = useState(true);
  const [isBillCreationLoading, setIsBillCreationLoading] = useState(false);
  const [initialCounts, setInitialCounts] = useState({
    containerCount: '',
    threadCount: '',
    boxPolyCount: '',
  });

  // Generation updates the baseline counts too; derive this instead of keeping
  // a second state value that misses the baseline update.
  const allEmpty =
    (!containerCount || containerCount === '0') &&
    (!threadCount || threadCount === '0') &&
    (!boxPolyCount || boxPolyCount === '0');
  const isGenerateDisabled = allEmpty ||
    (containerCount === initialCounts.containerCount &&
      threadCount === initialCounts.threadCount &&
      boxPolyCount === initialCounts.boxPolyCount);

  const generateEntries = () => {
    const createEntries = (count, type, existingData = []) =>
      Array.from({ length: count }).map(
        (_, index) => existingData[index] || { type, name: '', qty: '', unit_price: '' },
      );

    setContainerData(createEntries(Number(containerCount), 'Container', containerData));
    setThreadData(createEntries(Number(threadCount), 'Thread', threadData));
    setBoxPolyData(createEntries(Number(boxPolyCount), 'Box/Polybag', boxPolyData));
    setInitialCounts({ containerCount, threadCount, boxPolyCount });
  };

  const handleChange = (index, field, value, type) => {
    const updateData = (data, setter) => {
      const updatedData = [...data];
      updatedData[index][field] = field === 'name' ? value : parseFloat(value) || '';
      setter(updatedData);
    };

    if (type === 'Container') updateData(containerData, setContainerData);
    if (type === 'Thread') updateData(threadData, setThreadData);
    if (type === 'Box/Polybag') updateData(boxPolyData, setBoxPolyData);
  };

  const calculateSubtotal = (qty, unitPrice) =>
    qty && unitPrice ? (qty * unitPrice).toFixed(2) : '';

  const calculateTotalCost = () => {
    const allData = [...containerData, ...threadData, ...boxPolyData];
    return allData.reduce((sum, entry) => sum + (entry.qty * entry.unit_price || 0), 0).toFixed(2);
  };
  const handleSubmit = () => {
    const mapData = (data, type) =>
      data.map((item) => ({
        name: item.name,
        type: type.toLowerCase(),
        qty: item.qty,
        unit_price: item.unit_price,
      }));
    const finalData = [
      ...mapData(containerData, 'Container'),
      ...mapData(threadData, 'Thread'),
      ...mapData(boxPolyData, 'Box_Poly'),
    ];
    if (!isBill) handleBillCreation(finalData);
    else handleLineUpdate(finalData, state);
    setShowEntry(false);
    setCostviewOpen(false);
  };

  const handleLineUpdate = (finalData, state) => {
    updateBillLine({
      data: { bill_id: state.id, line_create: true, data: finalData },
    })
      .then((response) => {
        popup('success', response.message);
        setLineData(response?.data?.records?.[0]?.invoice_line_ids);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleBillCreation = async (finalData) => {
    setIsBillCreationLoading(true);
    dispatchBillCreation({
      data: { dispatch_id: state?.id, data: finalData },
    })
      .then((response) => {
        setIsBillCreationLoading(false);
        if (response?.data?.records?.length > 0) {
          popup(POPUP_TYPE.SUCCESS, response.message);
          navigate(`${ROUTES.BILLS}/${response.data.records[0].name.replaceAll('/', '_')}`, {
            state: response.data.records[0],
          });
        } else {
          popup(POPUP_TYPE.ERR, response?.message);
        }
      })
      .catch((err) => {
        // popup(POPUP_TYPE.ERR, err.message);
        setIsBillCreationLoading(false);
        throw err;
      });
  };

  const handleCancel = () => {
    setContainerData([]);
    setThreadData([]);
    setBoxPolyData([]);
    setInitialCounts({
      containerCount: '',
      threadCount: '',
      boxPolyCount: '',
    });
  };

  if (!showEntry) return null;

  const renderTable = (data, type) =>
    data.length > 0 && (
      <table className="ups-table">
        <>
          <caption style={{ captionSide: 'top', fontSize: '1.2rem', fontWeight: 'bold' }}>
            {type} Entry
          </caption>
          <thead>
            <tr>
              <th style={{ minWidth: '120px' }}>Name</th>
              <th style={{ minWidth: '120px' }}>Quantity</th>
              <th style={{ minWidth: '120px' }}>Unit Price</th>
              <th style={{ minWidth: '120px' }}>Subtotal</th>
            </tr>
          </thead>
        </>
        <tbody>
          {data.map((entry, index) => (
            <tr key={`${type}-${index}`}>
              <td>
                <input
                  type="text"
                  className="form-control"
                  value={entry.name}
                  onChange={(e) => handleChange(index, 'name', e.target.value, type)}
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  value={entry.qty}
                  onChange={(e) => handleChange(index, 'qty', e.target.value, type)}
                  onWheel={(e) => e.target.blur()}
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  value={entry.unit_price}
                  onChange={(e) => handleChange(index, 'unit_price', e.target.value, type)}
                  onWheel={(e) => e.target.blur()}
                />
              </td>
              <td>{calculateSubtotal(entry.qty, entry.unit_price)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  const isSubmitDisabled =
    (containerData?.length > 0 &&
      containerData.some((row) => !row.name?.trim() || !row.qty || !row.unit_price)) ||
    (threadData?.length > 0 &&
      threadData.some((row) => !row.name?.trim() || !row.qty || !row.unit_price)) ||
    (boxPolyData?.length > 0 &&
      boxPolyData.some((row) => !row.name?.trim() || !row.qty || !row.unit_price));

  return (
    <>
      {!isBill && (
        <div className="d-flex align-items-center justify-content-end mt-3 mb-3 ">
          <Button className={`blue-btn px-3 py-2 ${isBillCreationLoading ? 'disabled' : ''}`} disabled={isBillCreationLoading} clickHandler={() => handleBillCreation([])}>
            {isBillCreationLoading && (
              <span
                className="spinner-border spinner-border-sm text-white me-2"
                role="status"
              />
            )}
            { isBillCreationLoading ? 'Creating...' : 'Create Bill Without Entry' }
          </Button>
        </div>
      )}
      <div className="ups-weight-container" style={{ height: '100vh', overflowY: 'auto' }}>
        <h2>Shipping and Packing Costs Entry</h2>
        <div className="ups-weight-input">
          <label>Containers:</label>
          <input
            type="number"
            className="form-control"
            min="0"
            max="100"
            value={containerCount}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (value >= 0 && value <= 100) {
                setContainerCount(value);
              } else if (e.target.value === '') {
                setContainerCount('');
              }
            }}
            onWheel={(e) => e.target.blur()}
          />
          <label>Threads:</label>
          <input
            type="number"
            className="form-control"
            min="0"
            max="100"
            value={threadCount}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (value >= 0 && value <= 100) {
                setThreadCount(value);
              } else if (e.target.value === '') {
                setThreadCount('');
              }
            }}
          />
          <label>Box/Polybags:</label>
          <input
            type="number"
            className="form-control"
            min="2"
            max="100"
            value={boxPolyCount}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (value >= 0 && value <= 100) {
                setBoxPolyCount(value);
              } else if (e.target.value === '') {
                setBoxPolyCount('');
              }
            }}
          />
        </div>
        <div className="d-flex align-items-center justify-content-around">
          <Button
            className={`blue-btn px-3 py-0 ${isGenerateDisabled ? 'disabled' : ''}`}
            clickHandler={generateEntries}
            disabled={isGenerateDisabled}>
            Generate Rows
          </Button>
          <Button className="blue-btn px-3 py-0" clickHandler={() => setCostviewOpen(false)}>
            Cancel
          </Button>
        </div>
        {renderTable(containerData, 'Container')}
        {renderTable(threadData, 'Thread')}
        {renderTable(boxPolyData, 'Box/Polybag')}
        {(containerData.length > 0 || threadData.length > 0 || boxPolyData.length > 0) && (
          <div className="d-flex justify-content-end align-items-center mt-3 px-4">
            <h4>Total: ${calculateTotalCost()}</h4>
          </div>
        )}
        {(containerData.length > 0 || threadData.length > 0 || boxPolyData.length > 0) && (
          <div className="d-flex justify-content-evenly align-items-center ups-button-container">
            <Button
              className={`blue-btn px-3 py-2 ${isSubmitDisabled ? 'disabled' : ''}`}
              clickHandler={handleSubmit}
              disabled={isSubmitDisabled}>
              Submit
            </Button>
            <Button className="blue-btn px-3 py-2" clickHandler={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default ContainerThreadBoxEntry;
